import { useMemo, useRef, useEffect, useState } from 'react';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useEventInfo } from '@shared/utils/eventInfo';
import { ScoringPriorityField, AutopopulateAccommodationPlacesScenario, Maybe } from '@graphql/generated';
import { addError } from '@shared/utils/logger';
import { useAutoPopulationApiV4 } from './api/useAutoPopulationApiV4';
import { useAutoPopulationApiV5 } from './api/useAutoPopulationApiV5';
import { useAutoPopulationPolling } from './hooks/useAutoPopulationPolling';
import { autoPopulationSummary as autoPopulationSummaryLocalStorage } from './storage/autoPopulationSummary';
import { useAutoPopulationFeatureFlags } from './hooks/useAutoPopulationFeatureFlags';
import { AutoPopulationPollingResult, UseAutoPopulationOptions, UseAutoPopulationReturn, StartAutoPopulationParams } from './types';

const OPERATION_NAME = 'useAutoPopulation';

// Default search radius in kilometers for accommodation auto-population
const AUTO_POPULATE_DEFAULT_RADIUS_KM = 5;

// Default fields used to score and prioritize accommodations during auto-population
const DEFAULT_SCORING_PRIORITY_FIELDS = [
  ScoringPriorityField.standardRoomPriceDiscountPercent,
  ScoringPriorityField.userRating,
  ScoringPriorityField.distanceFromSearchLocation,
] as const;

/**
 * Unified hook for auto-population of accommodations that handles both v4 (synchronous) and v5 (asynchronous) APIs.
 *
 * This hook automatically populates accommodations near a given location by calling the appropriate API version
 * based on feature flags. The v4 API returns results synchronously, while the v5 API initiates an async job
 * that must be polled for completion.
 *
 * @returns Hook return value containing start function, status, results, and helper methods
 */
export const useAutoPopulation = (options: UseAutoPopulationOptions): UseAutoPopulationReturn => {
  const { mode = 'auto', onComplete, context, validateIsEnoughTimeElapsed } = options;

  const [isStarting, setIsStarting] = useState(false);

  const { eventInfo } = useEventInfo();
  const eventId = eventInfo?.eventId;

  // Prevents duplicate onComplete callbacks when polling updates and effects trigger simultaneously
  const hasCompletedRef = useRef(false);
  const currentEventIdRef = useRef<Maybe<string>>(eventId ?? null);

  // Feature flags control v4/v5 API selection and polling behavior
  const {
    asyncHotelAutopopulationEnabled,
    isAccommodationsAiRecommendationsEnabled,
    hasEnoughTimeElapsed,
    isAutoPopulationDoneFromStorage,
    setIsAutoPopulationDone,
    isLoadingFeatureFlags,
  } = useAutoPopulationFeatureFlags(validateIsEnoughTimeElapsed);

  // API hooks: v4 (synchronous) vs v5 (async with polling)
  const { callAutoPopulationApiV4, loading: loadingV4 } = useAutoPopulationApiV4();
  const { callAutoPopulationApiV5, loading: loadingV5 } = useAutoPopulationApiV5();

  // Skip polling when: time validation fails, mode is start-only, async feature disabled, or no eventId
  const shouldSkipPolling = (validateIsEnoughTimeElapsed && !hasEnoughTimeElapsed) || mode === 'start-only' || !asyncHotelAutopopulationEnabled || !eventId;

  // Polling hook monitors v5 async job status and fetches results when complete
  const {
    status: pollStatus,
    results: pollResults,
    loading: loadingPoll,
    error: pollError,
    restartPolling,
  } = useAutoPopulationPolling({
    eventId,
    shouldSkipAutoPopulationCheck: shouldSkipPolling,
    context,
  });

  // Reset state when event changes to prevent stale data
  useEffect(() => {
    const newEventId = eventId ?? null;
    if (currentEventIdRef.current !== newEventId) {
      currentEventIdRef.current = newEventId;
      hasCompletedRef.current = false;
      setIsStarting(false);
    }
  }, [eventId]);

  // Trigger onComplete callback when polling completes successfully
  useEffect(() => {
    if (hasCompletedRef.current || mode === 'start-only') return;
    if (pollStatus === 'COMPLETED' && pollResults.length > 0 && onComplete) {
      hasCompletedRef.current = true;
      onComplete(pollResults);
    }
  }, [pollStatus, pollResults, mode, onComplete]);

  // Combine loading states from APIs, polling, and feature flags
  const isLoading = loadingV4 || loadingV5 || loadingPoll || isLoadingFeatureFlags;

  // Derive unified status: Priority is loading states > v4 fallback (always completed) > polling status
  const status = useMemo(() => {
    if (isLoading || isStarting) return 'PENDING';
    // Skip polling if async hotel autopopulation is disabled or if we are in poll-only mode and should skip polling
    if (!asyncHotelAutopopulationEnabled || (shouldSkipPolling && mode === 'poll-only')) return 'COMPLETED';
    return pollStatus;
  }, [isLoading, isStarting, asyncHotelAutopopulationEnabled, pollStatus, shouldSkipPolling, mode]);

  const isCompletedFromApi = status === 'COMPLETED';

  // Start auto-population: validates params, calls appropriate API (v4 or v5), handles results
  const startAutoPopulation = useEventCallback(async (params: StartAutoPopulationParams) => {
    if (isStarting) return;

    const targetEventId = params.eventId ?? eventId;
    if (!targetEventId) {
      addError(new Error('Missing event id'), { context, operation: OPERATION_NAME });
      return;
    }

    const { venue, searchRadiusKm = AUTO_POPULATE_DEFAULT_RADIUS_KM, scoringPriorityFields = DEFAULT_SCORING_PRIORITY_FIELDS, scenario } = params;

    if (typeof venue.latitude !== 'number' || typeof venue.longitude !== 'number') {
      addError(new Error('Missing location'), { context, eventId: targetEventId, operation: OPERATION_NAME });
      return;
    }

    setIsStarting(true);
    hasCompletedRef.current = false;

    try {
      if (asyncHotelAutopopulationEnabled) {
        // V5: Async API with polling

        // Use provided scenario, or default to eventCreate if AI recommendations are enabled.
        // If undefined, the backend will use its default scenario (not specific to AI recommendations).
        let finalScenario: AutopopulateAccommodationPlacesScenario | undefined = scenario;
        if (!finalScenario && isAccommodationsAiRecommendationsEnabled) {
          finalScenario = AutopopulateAccommodationPlacesScenario.eventCreate;
        }
        const v5Response = await callAutoPopulationApiV5({
          eventId: targetEventId,
          location: { latitude: venue.latitude, longitude: venue.longitude },
          scoringPriorityFields,
          context,
          searchRadiusKm,
          venueGooglePlaceId: venue.googlePlaceId ?? undefined,
          scenario: finalScenario,
        });

        if (v5Response?.error) {
          addError(new Error(v5Response.error), { context, eventId: targetEventId, isAsync: true, operation: 'v5Response' });
          return;
        }

        // Restart polling to fetch async job results; 'start-only' mode skips this
        if (mode === 'auto') {
          restartPolling();
        }
      } else {
        // V4: Synchronous API with immediate results
        const v4Response = await callAutoPopulationApiV4({
          eventId: targetEventId,
          location: { latitude: venue.latitude, longitude: venue.longitude },
          scoringPriorityFields,
          context,
          searchRadiusKm,
        });

        // V4 stores summary in localStorage; v5 derives it from polling results
        autoPopulationSummaryLocalStorage.set(targetEventId, { newlyPopulatedCount: v4Response.length, alreadyExistsCount: params.existingAccommodationsCount ?? 0 });

        // Map v4 response to unified result format
        const results: AutoPopulationPollingResult[] = v4Response.map(place => ({
          accommodationPlaceId: place.accommodationPlaceId,
          proposedNoteFromHostToGuest: place.proposedNoteFromHostToGuest,
          whyThisHotelIsAGreatPick: place.whyThisHotelIsAGreatPick,
        }));

        hasCompletedRef.current = true;
        onComplete?.(results);
      }
    } catch (error) {
      addError(error instanceof Error ? error : new Error('Auto-population failed'), { context, eventId: targetEventId, operation: OPERATION_NAME });
    } finally {
      setIsStarting(false);
    }
  });

  // Manual completion handler for legacy code compatibility
  const handleOnComplete = useEventCallback(() => {
    if (hasCompletedRef.current) return;
    if (isCompletedFromApi && pollResults.length > 0) {
      hasCompletedRef.current = true;
      onComplete?.(pollResults);
    }

    // Set localStorage flag for v4 backwards compatibility
    if (!asyncHotelAutopopulationEnabled) {
      setIsAutoPopulationDone();
    }
  });

  // Determine if auto-population is completed (combines v4 localStorage check with v5 API status)
  // Used to prevent re-population when location changes if already completed
  const isAutoPopulationDone = useMemo(() => {
    // If v5 disabled or loading, use v4 localStorage check
    if (!asyncHotelAutopopulationEnabled || isLoadingFeatureFlags) {
      return isAutoPopulationDoneFromStorage;
    }

    // If no v5 operations found, fallback to v4 localStorage
    if (status === 'IDLE' || (status === 'COMPLETED' && pollResults.length === 0)) {
      return isAutoPopulationDoneFromStorage;
    }

    // Check both v5 API completion and v4 localStorage for backwards compatibility
    return (isCompletedFromApi && pollResults.length > 0) || isAutoPopulationDoneFromStorage;
  }, [asyncHotelAutopopulationEnabled, isLoadingFeatureFlags, status, isCompletedFromApi, pollResults.length, isAutoPopulationDoneFromStorage]);

  // Auto-population summary helpers for displaying counts in UI
  const autoPopulationSummary = useMemo(
    () => ({
      get: (existingCount?: number) => {
        if (!eventId || isLoadingFeatureFlags || isLoading) {
          return { newlyPopulatedCount: 0, alreadyExistsCount: 0 };
        }

        // V5: derive summary from polling results
        if (asyncHotelAutopopulationEnabled && pollResults.length > 0) {
          const alreadyExists = existingCount ? Math.max(0, existingCount - pollResults.length) : 0;
          return { newlyPopulatedCount: pollResults.length, alreadyExistsCount: alreadyExists };
        }

        // V4: get summary from localStorage
        return autoPopulationSummaryLocalStorage.get(eventId) ?? { newlyPopulatedCount: 0, alreadyExistsCount: 0 };
      },
      set: (counts: { newlyPopulatedCount: number; alreadyExistsCount?: number }) => {
        if (eventId) autoPopulationSummaryLocalStorage.set(eventId, counts);
      },
      remove: () => {
        if (eventId) autoPopulationSummaryLocalStorage.remove(eventId);
      },
    }),
    [eventId, isLoadingFeatureFlags, isLoading, asyncHotelAutopopulationEnabled, pollResults.length],
  );

  return {
    startAutoPopulation,
    onComplete: handleOnComplete,
    callAutoPopulationApiV4,
    autoPopulationSummary,
    status,
    isAutoPopulationDone,
    results: pollResults,
    error: pollError,
  };
};
